import {
  addToast,
  Button,
  ErrorToast,
  SuccessToast,
} from '@otter-ai/den-components';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { FormattedText } from '@/shared/components/FormattedText';
import { useApplications } from '@/shared/hooks/useApplications';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { descriptionFormatRules } from '../../shared/utils/text';
import { AppRowLayout } from '../components/AppRowLayout';
import { useDisconnectApplication } from '../hooks/useDisconnectApplication';
import { useJiraOAuth } from '../hooks/useJiraOAuth';
import { DisconnectedButton } from './DisconnectedButton';

/**
 * The row for Jira on the apps settings page.
 */
const APP_NAME = 'jira-mcp';

export function JiraAppRow() {
  const t = useTranslations('settings:integrations.apps.jira');
  const tCommon = useTranslations('settings:integrations');
  const { isApplicationConnected, isLoading: isLoadingStatus } =
    useApplications();
  const { mutateAsync: disconnectApp, isPending: isDisconnecting } =
    useDisconnectApplication(APP_NAME);
  const { openJiraAuthorizationPage } = useJiraOAuth();

  const isConnected = isApplicationConnected(APP_NAME);

  const onClickConnect = useCallback(async () => {
    try {
      openJiraAuthorizationPage();
    } catch {
      addToast(
        <ErrorToast
          description={tCommon('connectFailed', { appName: 'Jira' })}
        />,
      );
    }
  }, [openJiraAuthorizationPage, tCommon]);

  const handleDisconnect = useCallback(async () => {
    const confirmed = await openConfirmDialog({
      title: t('disconnectModal.title'),
      message: (
        <FormattedText>
          {t.rich('disconnectModal.description', descriptionFormatRules())}
        </FormattedText>
      ),
      confirmText: t('disconnectModal.confirmText'),
      cancelText: t('disconnectModal.cancelText'),
      danger: true,
    });

    if (!confirmed) {
      return true;
    }

    try {
      await disconnectApp();
      addToast(<SuccessToast description={t('successfulDisconnected')} />);
      return true;
    } catch {
      addToast(<ErrorToast description={t('failedDisconnect')} />);
      return false;
    }
  }, [t, disconnectApp]);

  const description = (
    <FormattedText>
      {t.rich('description', descriptionFormatRules({}))}
    </FormattedText>
  );

  return (
    <AppRowLayout
      appRowId="jira-app"
      // TODO(yedi): Migrate to den-icons
      iconUrl="https://public.otter.ai/img/icon-jira.svg"
      title="Jira"
      description={description}
      buttons={
        isLoadingStatus || isDisconnecting ? (
          <Button disabled>Loading...</Button>
        ) : isConnected ? (
          <DisconnectedButton
            disconnectButtonText={t('disconnectButton')}
            disconnectAction={handleDisconnect}
            tooltipCaption={t('disconnectTooltip')}
          />
        ) : (
          <Button onClick={onClickConnect}>{t('connectButton')}</Button>
        )
      }
    />
  );
}
